import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Plug, Unplug, Film, ExternalLink, RefreshCw, Sparkles, Search } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { appConfirm } from "@/components/AppDialogHost";

/**
 * Multi-provider Avatar Studio workspace.
 *
 * Renders one provider at a time (HeyGen or D-ID). Synthesia keeps its
 * existing dedicated UI in AvatarStudio.jsx — this component is purpose-
 * built for the new providers and stays lean: connect, browse catalog,
 * render, poll, download.
 *
 * Props:
 *   providerKey: 'heygen' | 'did'
 *   onProviderChange?: (key) => void    optional callback after connect/disconnect
 */
export default function ProviderRenderPanel({ providerKey, onProviderChange }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [keyInput, setKeyInput] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [avatars, setAvatars] = useState(null);
  const [voices, setVoices] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [form, setForm] = useState({ title: "", script: "", avatar_id: "", voice_id: "" });
  const [rendering, setRendering] = useState(false);
  const [videos, setVideos] = useState([]);
  const [pollingId, setPollingId] = useState(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/avatar-studio/providers/${providerKey}/status`);
      setStatus(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load status");
    } finally { setLoading(false); }
  }, [providerKey]);

  const loadCatalog = useCallback(async () => {
    if (!status?.connected) return;
    setCatalogLoading(true);
    try {
      const [a, v] = await Promise.all([
        api.get(`/avatar-studio/providers/${providerKey}/avatars`),
        api.get(`/avatar-studio/providers/${providerKey}/voices`),
      ]);
      setAvatars(a.data.items || []);
      setVoices(v.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Couldn't load ${providerKey} catalog`);
    } finally { setCatalogLoading(false); }
  }, [providerKey, status?.connected]);

  useEffect(() => { setAvatars(null); setVoices(null); loadStatus(); }, [providerKey, loadStatus]);
  useEffect(() => { if (status?.connected && avatars === null) loadCatalog(); }, [status?.connected, avatars, loadCatalog]);

  const connect = async (e) => {
    e?.preventDefault?.();
    if (!keyInput.trim()) return;
    setConnecting(true);
    try {
      const r = await api.post(`/avatar-studio/providers/${providerKey}/connect`, { api_key: keyInput.trim() });
      if (r.data?.warning) toast.warning(r.data.warning, { duration: 8000 });
      else toast.success(`${providerLabel(providerKey)} connected`);
      setKeyInput("");
      await loadStatus();
      onProviderChange?.(providerKey);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not connect — check the key");
    } finally { setConnecting(false); }
  };

  const disconnect = async () => {
    if (!await appConfirm(`Disconnect ${providerLabel(providerKey)}? New renders will be blocked.`)) return;
    try {
      await api.delete(`/avatar-studio/providers/${providerKey}/connect`);
      toast.success("Disconnected");
      setAvatars(null); setVoices(null);
      await loadStatus();
      onProviderChange?.(providerKey);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    }
  };

  const render = async () => {
    if (!form.script.trim()) { toast.error("Script is required"); return; }
    if (!form.avatar_id) { toast.error("Pick an avatar"); return; }
    if (providerKey === "heygen" && !form.voice_id) { toast.error("Pick a voice"); return; }
    setRendering(true);
    try {
      const r = await api.post(`/avatar-studio/providers/${providerKey}/videos`, {
        title: form.title || "Untitled",
        script: form.script,
        avatar_id: form.avatar_id,
        voice_id: form.voice_id,
      });
      toast.success("Render queued — polling for completion");
      const item = { ...r.data, created_at: new Date().toISOString() };
      setVideos((prev) => [item, ...prev]);
      setPollingId(item.video_id);
      setForm({ title: "", script: "", avatar_id: "", voice_id: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Render failed");
    } finally { setRendering(false); }
  };

  // Poll the active render every 8s until terminal
  useEffect(() => {
    if (!pollingId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get(`/avatar-studio/providers/${providerKey}/videos/${pollingId}`);
        if (cancelled) return;
        setVideos((prev) => prev.map((v) =>
          v.video_id === pollingId ? { ...v, ...r.data } : v));
        if (["complete", "failed", "cancelled"].includes(r.data?.status)) {
          setPollingId(null);
          if (r.data.status === "complete") toast.success("Render ready");
          else if (r.data.status === "failed") toast.error(r.data.error || "Render failed");
        }
      } catch { /* keep polling — transient */ }
    };
    const id = setInterval(tick, 8000);
    tick();
    return () => { cancelled = true; clearInterval(id); };
  }, [pollingId, providerKey]);

  if (loading) {
    return <div className="rounded-xl border border-line bg-bg-surface p-6 flex items-center gap-2 text-sm text-ink-muted">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading {providerLabel(providerKey)}…
    </div>;
  }

  if (!status?.connected) {
    return <ConnectCard providerKey={providerKey} keyInput={keyInput} setKeyInput={setKeyInput} onSubmit={connect} connecting={connecting} />;
  }

  const filteredAvatars = (avatars || []).filter((a) =>
    !filterText || (a.name || "").toLowerCase().includes(filterText.toLowerCase()));
  const filteredVoices = (voices || []).filter((v) =>
    !filterText || `${v.name || ""} ${v.language || ""}`.toLowerCase().includes(filterText.toLowerCase()));

  return (
    <div className="space-y-4" data-testid={`provider-panel-${providerKey}`}>
      {/* Connection bar */}
      <div className="rounded-xl border border-line bg-bg-surface p-4 flex items-center gap-3">
        <div className="h-10 w-10 rounded-md grid place-items-center bg-emerald-500/10 text-emerald-300">
          <Plug className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-sm font-bold">{providerLabel(providerKey)} · BYOK · Connected</div>
          <div className="text-[11px] text-ink-muted">
            {(avatars?.length ?? 0)} avatars · {(voices?.length ?? 0)} voices · {status?.video_count || 0} videos rendered
          </div>
        </div>
        <button
          onClick={loadCatalog}
          disabled={catalogLoading}
          data-testid={`${providerKey}-refresh-catalog`}
          className="text-[11px] inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-line text-ink-secondary hover:text-ink-primary"
        >
          {catalogLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Refresh
        </button>
        <button
          onClick={disconnect}
          data-testid={`${providerKey}-disconnect-btn`}
          className="text-[11px] inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-brand-onair/30 text-brand-onair hover:bg-brand-onair/5"
        >
          <Unplug className="h-3.5 w-3.5" /> Disconnect
        </button>
      </div>

      {/* Catalog + composer */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr,1fr] gap-4">
        {/* Composer */}
        <div className="rounded-xl border border-line bg-bg-surface p-5 space-y-4">
          <div className="text-[10px] font-mono uppercase tracking-wider text-brand-amber">// COMPOSE</div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">Title</label>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Top of the Hour News"
              data-testid={`${providerKey}-title-input`}
              className="w-full mt-1 rounded-md bg-bg-elevated border border-line px-3 py-2 text-sm focus:border-brand-amber/60 focus:outline-none"
            />
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">Script</label>
            <textarea
              value={form.script}
              onChange={(e) => setForm({ ...form, script: e.target.value })}
              rows={6}
              placeholder="Paste or write a script…"
              data-testid={`${providerKey}-script-input`}
              className="w-full mt-1 rounded-md bg-bg-elevated border border-line px-3 py-2 text-sm focus:border-brand-amber/60 focus:outline-none font-mono"
            />
            <div className="text-[10px] text-ink-muted text-right">{form.script.length} / 5000 chars</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">Selected avatar</label>
              <div data-testid={`${providerKey}-selected-avatar`} className="mt-1 rounded-md bg-bg-elevated border border-line px-3 py-2 text-xs text-ink-secondary truncate">
                {form.avatar_id || "— pick from catalog →"}
              </div>
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">Selected voice</label>
              <div data-testid={`${providerKey}-selected-voice`} className="mt-1 rounded-md bg-bg-elevated border border-line px-3 py-2 text-xs text-ink-secondary truncate">
                {form.voice_id || "— pick from catalog →"}
              </div>
            </div>
          </div>
          <button
            onClick={render}
            disabled={rendering || !form.script || !form.avatar_id}
            data-testid={`${providerKey}-render-btn`}
            className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-bold bg-brand-amber text-bg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {rendering ? <><Loader2 className="h-4 w-4 animate-spin" /> Queueing…</> : <><Sparkles className="h-4 w-4" /> Render with {providerLabel(providerKey)}</>}
          </button>
        </div>

        {/* Catalog */}
        <div className="rounded-xl border border-line bg-bg-surface p-5 space-y-4">
          <div className="flex items-center gap-3">
            <div className="text-[10px] font-mono uppercase tracking-wider text-brand-amber flex-1">// CATALOG</div>
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none" />
              <Input
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder="Search…"
                data-testid={`${providerKey}-catalog-search`}
                className="pl-7 h-8 text-xs w-44"
              />
            </div>
          </div>

          {/* Avatars grid */}
          <div>
            <div className="text-[10px] text-ink-muted mb-2">AVATARS · {filteredAvatars.length}</div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[260px] overflow-y-auto pr-1" data-testid={`${providerKey}-avatar-grid`}>
              {(catalogLoading && !avatars) ? (
                <div className="col-span-full flex items-center justify-center py-8 text-xs text-ink-muted">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading avatars…
                </div>
              ) : filteredAvatars.length === 0 ? (
                <div className="col-span-full text-xs text-ink-muted italic text-center py-6">
                  No avatars{filterText ? " matching that search" : ""}.
                </div>
              ) : filteredAvatars.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setForm({ ...form, avatar_id: a.id })}
                  data-testid={`${providerKey}-avatar-tile-${a.id}`}
                  className={`group relative rounded-md overflow-hidden border transition ${form.avatar_id === a.id ? "border-brand-amber ring-2 ring-brand-amber/40" : "border-line hover:border-brand-amber/60"}`}
                >
                  <div className="aspect-square bg-bg-elevated">
                    {a.preview_url ? (
                      <img src={a.preview_url} alt={a.name} loading="lazy" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-ink-muted text-xs">{(a.name || "?")[0]}</div>
                    )}
                  </div>
                  <div className="px-1.5 py-1 text-[10px] text-ink-primary truncate text-left">{a.name}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Voices list */}
          <div>
            <div className="text-[10px] text-ink-muted mb-2">VOICES · {filteredVoices.length}</div>
            <div className="space-y-1 max-h-[180px] overflow-y-auto pr-1" data-testid={`${providerKey}-voice-list`}>
              {(catalogLoading && !voices) ? (
                <div className="flex items-center justify-center py-4 text-xs text-ink-muted">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading voices…
                </div>
              ) : filteredVoices.length === 0 ? (
                <div className="text-xs text-ink-muted italic text-center py-4">No voices{filterText ? " matching that search" : ""}.</div>
              ) : filteredVoices.slice(0, 60).map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setForm({ ...form, voice_id: v.id })}
                  data-testid={`${providerKey}-voice-row-${v.id}`}
                  className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded text-xs transition ${form.voice_id === v.id ? "bg-brand-amber/15 border border-brand-amber/40" : "hover:bg-bg-elevated border border-transparent"}`}
                >
                  <span className="flex-1 truncate text-ink-primary">{v.name}</span>
                  {v.language && <span className="text-[10px] font-mono text-ink-muted">{v.language}</span>}
                  {v.gender && <span className="text-[10px] text-ink-muted uppercase">{v.gender}</span>}
                </button>
              ))}
              {filteredVoices.length > 60 && (
                <div className="text-[10px] text-ink-muted text-center pt-1">…and {filteredVoices.length - 60} more — refine your search to see them.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent renders */}
      <div className="rounded-xl border border-line bg-bg-surface p-5">
        <div className="text-[10px] font-mono uppercase tracking-wider text-brand-amber mb-3">// RECENT RENDERS</div>
        {!videos.length ? (
          <div className="text-xs text-ink-muted italic" data-testid={`${providerKey}-no-videos`}>
            No renders yet this session. Queue one above.
          </div>
        ) : (
          <div className="space-y-2" data-testid={`${providerKey}-videos-list`}>
            {videos.map((v) => (
              <div key={v.video_id} className="flex items-center gap-3 p-2 rounded-md bg-bg-elevated border border-line">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-ink-primary truncate">{v.title || "(untitled)"}</div>
                  <div className="text-[10px] text-ink-muted font-mono">
                    {v.video_id} · {v.status}{v.duration_sec ? ` · ${v.duration_sec}s` : ""}
                  </div>
                </div>
                {v.status === "in_progress" || v.status === "queued" ? (
                  <Loader2 className="h-4 w-4 animate-spin text-brand-amber" />
                ) : v.status === "complete" && v.download_url ? (
                  <a
                    href={v.download_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`${providerKey}-video-download-${v.video_id}`}
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-brand-amber text-bg font-semibold"
                  >
                    <ExternalLink className="h-3 w-3" /> Open
                  </a>
                ) : v.status === "failed" ? (
                  <span className="text-[10px] text-rose-400 max-w-xs truncate">{v.error || "failed"}</span>
                ) : (
                  <Film className="h-4 w-4 text-ink-muted" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function providerLabel(key) {
  return { heygen: "HeyGen", did: "D-ID", synthesia: "Synthesia" }[key] || key;
}


function ConnectCard({ providerKey, keyInput, setKeyInput, onSubmit, connecting }) {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    api.get("/avatar-studio/providers").then((r) => {
      const m = (r.data?.items || []).find((p) => p.provider === providerKey);
      setMeta(m || null);
    }).catch(() => {});
  }, [providerKey]);
  return (
    <div className="rounded-xl border border-dashed border-line bg-bg-elevated p-6 space-y-4" data-testid={`${providerKey}-connect-card`}>
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-md grid place-items-center bg-brand-amber/10 text-brand-amber">
          <Unplug className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="font-display font-bold text-base">{providerLabel(providerKey)} · BYOK</div>
          <div className="text-xs text-ink-muted">{meta?.description || "Bring your own API key."}</div>
          {meta?.key_format_hint && <div className="text-[10px] text-ink-muted mt-1 font-mono">{meta.key_format_hint}</div>}
        </div>
        {meta?.key_help_url && (
          <a
            href={meta.key_help_url}
            target="_blank"
            rel="noreferrer"
            data-testid={`${providerKey}-get-key-link`}
            className="text-[11px] inline-flex items-center gap-1 text-brand-amber hover:underline"
          >
            Get a key <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
      <form onSubmit={onSubmit} className="flex gap-2">
        <input
          type="password"
          value={keyInput}
          onChange={(e) => setKeyInput(e.target.value)}
          placeholder={`Paste ${providerLabel(providerKey)} API key`}
          data-testid={`${providerKey}-key-input`}
          className="flex-1 rounded-md bg-bg-surface border border-line px-3 py-2 text-sm font-mono focus:border-brand-amber/60 focus:outline-none"
        />
        <button
          type="submit"
          disabled={connecting || !keyInput.trim()}
          data-testid={`${providerKey}-connect-submit`}
          className="inline-flex items-center gap-1 px-4 py-2 rounded-md text-sm font-bold bg-brand-amber text-bg disabled:opacity-50"
        >
          {connecting ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Connecting…</> : <><Plug className="h-3.5 w-3.5" /> Connect</>}
        </button>
      </form>
    </div>
  );
}
